// src/services/groupService.js
import { domain } from "../auth/domain";

const getAuthHeaders = () => {
  const token = localStorage.getItem("accessToken");
  return {
    "Content-Type": "application/json",
    Authorization: `JWT ${token}`,
  };
};

const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.json();
    if (error?.description) {
      const detailParts = [];
      if (error?.nmb_error?.http_status) detailParts.push(`HTTP ${error.nmb_error.http_status}`);
      if (error?.nmb_error?.error_type) detailParts.push(error.nmb_error.error_type);
      if (error?.nmb_error?.endpoint) detailParts.push(error.nmb_error.endpoint);
      const detailText = detailParts.length ? ` (${detailParts.join(" - ")})` : "";
      throw new Error(`${error.description}${detailText}`);
    }
    if (error?.detail) {
      throw new Error(error.detail);
    }
    // Create a more detailed error message for the user from backend validation
    const errorMessages = Object.entries(error)
      .map(([key, value]) => {
        const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        const message = Array.isArray(value)
          ? value.join(', ')
          : typeof value === "object" && value !== null
            ? Object.values(value).join(', ')
            : value;
        return `${formattedKey}: ${message}`;
      })
      .join('; ');
    throw new Error(errorMessages || "An API error occurred");
  }
  if (response.status === 204) return null; // Handle success with no content
  return response.json();
};

export const fetchGroups = async (options = {}) => {
  const response = await fetch(`${domain}groups/`, {
    headers: getAuthHeaders(),
    signal: options.signal,
  });
  return handleResponse(response);
};

export const createGroup = async (groupData) => {
  const payload = { ...groupData };
  if (payload.contribution !== undefined) {
    payload.contribution_type = payload.contribution;
    delete payload.contribution;
  }
  if (payload.entry_fee_amount !== undefined) {
    payload.entry_fee_amount = Number(payload.entry_fee_amount);
  }

  const response = await fetch(`${domain}groups/`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
};

export const deleteGroup = async (groupId) => {
  const response = await fetch(`${domain}groups/${groupId}/`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

export const updateGroup = async (groupId, groupData) => {
  const payload = { ...groupData };
  if (payload.entry_fee_amount !== undefined) {
    payload.entry_fee_amount = Number(payload.entry_fee_amount);
  }
  const response = await fetch(`${domain}groups/${groupId}/`, {
    method: "PATCH", // Use PATCH for partial updates
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
};

export const fetchGroupNmbCredential = async (groupId) => {
  const response = await fetch(`${domain}groups/${groupId}/nmb-credential/`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

export const saveGroupNmbCredential = async (groupId, payload, exists = false) => {
  const response = await fetch(`${domain}groups/${groupId}/nmb-credential/`, {
    method: exists ? "PATCH" : "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
};

export const rotateGroupNmbPassword = async (groupId, password) => {
  const response = await fetch(`${domain}groups/${groupId}/nmb-credential/rotate-password/`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ password }),
  });
  return handleResponse(response);
};

export const verifyGroupNmbCredential = async (groupId) => {
  const response = await fetch(`${domain}groups/${groupId}/nmb-credential/verify/`, {
    method: "POST",
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

export const activateGroupNmbCredential = async (groupId) => {
  const response = await fetch(`${domain}groups/${groupId}/nmb-credential/activate/`, {
    method: "POST",
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

export const deactivateGroupNmbCredential = async (groupId) => {
  const response = await fetch(`${domain}groups/${groupId}/nmb-credential/deactivate/`, {
    method: "POST",
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

export const fetchGroupTerms = async () => {
  const response = await fetch(`${domain}profiles/terms/`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

export const closeTerm = async (termId) => {
  const response = await fetch(`${domain}profiles/terms/${termId}/close/`, {
    method: "POST",
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

export const renewTerm = async (data) => {
  const response = await fetch(`${domain}profiles/terms/renew/`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse(response);
};

export const fetchGroupTermStatus = async (groupId) => {
  const response = await fetch(`${domain}groups/${groupId}/term-status/`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

export const closeGroupTerm = async (groupId) => {
  const response = await fetch(`${domain}groups/${groupId}/term-close/`, {
    method: "POST",
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

export const renewGroupTerm = async (groupId, data) => {
  const response = await fetch(`${domain}groups/${groupId}/term-renew/`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse(response);
};
